<template>
  <div ref="dropdownRef" class="base-dropdown">
    <button
      type="button"
      :disabled="disabled"
      class="dropdown-trigger"
      :class="{ 'is-disabled': disabled }"
      @click="toggle"
    >
      <div class="dropdown-trigger__content">
        <span class="dropdown-trigger__header" :class="{ 'dropdown-trigger__header--small': multiple }">
          {{ headerText }}
        </span>

        <span class="dropdown-trigger__selected">
          {{ $t(selectedItemsText || '') }}
        </span>
      </div>
      <svg
        class="dropdown-trigger__icon"
        :class="{ 'is-open': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <Transition name="dropdown-fade">
      <div v-if="isOpen" class="dropdown-menu" @click.stop>
        <div class="dropdown-menu__content">
          <button v-if="multiple" type="button" class="dropdown-item dropdown-item--all" @click="selectAll">
            <span class="dropdown-item__label">{{ $t('rentFilter_all') }}</span>
            <div class="dropdown-checkbox" :class="{ 'is-checked': isAllSelected || isIndeterminate }">
              <svg
                v-if="isIndeterminate"
                class="dropdown-checkbox__icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M20 12H4" />
              </svg>
              <svg
                v-else-if="isAllSelected"
                class="dropdown-checkbox__icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </button>

          <button
            v-for="item in items"
            :key="item.id"
            type="button"
            :disabled="item.disabled"
            class="dropdown-item"
            :class="{
              'is-disabled': item.disabled,
              'is-selected': isSelected(item.id) && !multiple
            }"
            @click="!item.disabled && selectItem(item.id)"
          >
            <span class="dropdown-item__label">{{ $t(item.label) }}</span>

            <div v-if="multiple" class="dropdown-checkbox" :class="{ 'is-checked': isSelected(item.id) }">
              <svg
                v-if="isSelected(item.id)"
                class="dropdown-checkbox__icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <svg
              v-if="!multiple && isSelected(item.id)"
              class="dropdown-item__checkmark"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const { t } = useI18n();
export interface DropdownItem {
  id: string | number;
  label: string;
  disabled?: boolean;
}

export interface Props {
  items: DropdownItem[];
  modelValue?: string | number | (string | number)[];
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  multiple: false,
  placeholder: 'Select option',
  disabled: false
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | (string | number)[] | undefined];
}>();

const dropdownRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);

const headerText = computed(() => {
  return props.placeholder;
});

// const filteredItems = computed(() => {
//   const itemsToIgnore = ['all'];
//   return props.items.filter((item) => {
//     if (props.multiple) {
//       const itemLabelLower = String(item.label).toLowerCase();
//       return !itemsToIgnore.some((ignored) => ignored === itemLabelLower);
//     }
//     return true;
//   });
// });

const selectedItemsText = computed(() => {
  if (!props.modelValue || (Array.isArray(props.modelValue) && props.modelValue.length === 0)) {
    return `${t('coreSelect')}...`;
  }

  if (props.multiple && Array.isArray(props.modelValue)) {
    const selectedValues = props.modelValue;
    const count = selectedValues.length;

    if (count === 0) return null;
    if (count === props.items.length) return `${t('rentFilter_all')} (${count}/${count})`;

    const MAX_VISIBLE_ITEMS = 1;

    const selectedLabels = props.items.filter((item) => selectedValues.includes(item.id)).map((item) => item.label);

    if (count <= MAX_VISIBLE_ITEMS) {
      return selectedLabels.join(', ');
    } else {
      const visibleLabels = selectedLabels.slice(0, MAX_VISIBLE_ITEMS).join(', ');
      const remainingCount = count - MAX_VISIBLE_ITEMS;
      return `${visibleLabels}, +${remainingCount}`;
    }
  }

  const selectedItem = props.items.find((item) => item.id === props.modelValue);
  return selectedItem?.label || null;
});

const isSelected = (itemId: string | number): boolean => {
  if (props.multiple && Array.isArray(props.modelValue)) {
    return props.modelValue.includes(itemId);
  }
  return props.modelValue === itemId;
};

const isAllSelected = computed(() => {
  if (!props.multiple || !Array.isArray(props.modelValue)) return false;
  return props.modelValue.length === props.items.length;
});

const isIndeterminate = computed(() => {
  if (!props.multiple || !Array.isArray(props.modelValue)) return false;
  const count = props.modelValue.length;
  return count > 0 && count < props.items.length;
});

const toggle = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

const close = () => {
  isOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (dropdownRef.value && !dropdownRef.value.contains(target)) {
    close();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

const selectItem = (itemId: string | number) => {
  if (props.multiple) {
    const currentValue = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    const index = currentValue.indexOf(itemId);

    if (index > -1) {
      currentValue.splice(index, 1);
    } else {
      currentValue.push(itemId);
    }

    emit('update:modelValue', currentValue);
  } else {
    if (props.modelValue === itemId) {
      emit('update:modelValue', undefined);
    } else {
      emit('update:modelValue', itemId);
    }
    close();
  }
};

const selectAll = () => {
  if (!props.multiple) return;

  if (isAllSelected.value || isIndeterminate.value) {
    emit('update:modelValue', []);
  } else {
    const allIds = props.items.map((item) => item.id);
    emit('update:modelValue', allIds);
  }
};
</script>

<style lang="scss" scoped>
.base-dropdown {
  position: relative;
  display: inline-block;
  text-align: left;
  width: 100%;

  min-width: 165px;
  max-width: 200px;
}

.dropdown-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  text-align: left;
  background-color: rgba(0, 0, 0, 0.85);
  border: 1px solid #1e353e;
  border-radius: 8px;
  color: #ffffff;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    max-width: calc(100% - 28px);
  }

  &__header {
    font-family: Eczar;
    font-weight: 700;
    font-size: 16px;
    line-height: 1.5;
    color: #ffffff;
    transition:
      font-size 0.2s ease,
      color 0.2s ease;

    &--small {
      font-size: 12px;
      font-weight: 400;
      line-height: 2;
      color: #f1f1f1;
    }
  }

  &__selected {
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
    transition: font-size 0.2s ease;

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__icon {
    width: 20px;
    height: 20px;
    margin-left: 8px;
    flex-shrink: 0;
    transition: transform 0.2s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }
}

.dropdown-menu {
  position: absolute;
  z-index: 10;
  margin-top: 4px;
  width: 100%;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.85);
  border: 1px solid #1e353e;
  backdrop-filter: blur(60px);
  font-weight: 700;
  font-size: 16px;

  &__content {
    padding: 8px 0;
    max-height: 240px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #4c4d4f;
      border-radius: 3px;
    }
    scrollbar-width: thin;
    scrollbar-color: #4c4d4f transparent;
  }
}

.dropdown-item {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  color: #ffffff;
  background-color: transparent;
  border: none;
  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid #1e353e;
    margin-bottom: 0;
  }

  //   &:hover:not(.is-disabled) {
  //     background-color: #1f2937;
  //   }

  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  //   &.is-selected {
  //     background-color: #1f2937;
  //   }

  &--all {
    font-weight: 500;
    border-bottom: none;
  }

  &__label {
    flex: 1;
    text-align: start;
  }

  &__checkmark {
    width: 20px;
    height: 20px;
    margin-left: auto;
    color: #039ed0;
  }
}

.dropdown-checkbox {
  width: 14px;
  height: 14px;
  margin-left: auto;
  flex-shrink: 0;
  border-radius: 2px;
  border: 1px solid #4c4d4f;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &.is-checked {
    background-color: #039ed0;
    border-color: #ffffff;
  }

  &__icon {
    width: 12px;
    height: 12px;
    color: #ffffff;
  }
}

.dropdown-fade-enter-active {
  transition:
    opacity 0.1s ease-out,
    transform 0.1s ease-out;
}

.dropdown-fade-leave-active {
  transition:
    opacity 0.075s ease-in,
    transform 0.075s ease-in;
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
